<template>
  <Layout>
    <div v-if="showConfetti" class="confetti-overlay">
      <Vue3Lottie
          :animationData="confettiAnimation"
          :autoPlay="true"
          :loop="false"
          @onComplete="onConfettiComplete"
          ref="confettiRef"
      />
    </div>

    <div class="page-header">
      <div class="page-title">
        <h2 class="page-title-h2">{{ $t('marketplace_contents') }}</h2>
        <span class="page-title-desc">{{$t('marketplace_description')}}</span>
      </div>
    </div>

    <div class="marketplace-tabs">
      <div class="flex-row-div">
        <button
            class="tab-button"
            :class="{ 'active': (currentFilters.onlyCompanies === 0 || !currentFilters.onlyCompanies) && !currentFilters.tabType }"
            @click="switchTab(0)"
        >
          <span class="tab-text">{{$t("contents_open_to_everyone")}}</span>
        </button>

        <button
            class="tab-button"
            :class="{ 'active': currentFilters.onlyCompanies === 1 && !currentFilters.tabType }"
            @click="switchTab(1)"
        >
          <span class="tab-text">{{$t('created_by_me')}}</span>
        </button>

        <button
            class="tab-button"
            :class="{ 'active': currentFilters.tabType === 'comments' }"
            @click="switchTab('comments')"
        >
          <span class="tab-text">{{$t('comments')}}</span>
        </button>

        <button
            class="tab-button"
            :class="{ 'active': currentFilters.tabType === 'sales' }"
            @click="switchTab('sales')"
        >
          <span class="tab-text">{{$t('my_sales')}}</span>
        </button>

        <button
            class="tab-button"
            :class="{ 'active': currentFilters.tabType === 'purchased' }"
            @click="switchTab('purchased')"
        >
          <span class="tab-text">{{$t('my_purchases')}}</span>
        </button>
        <button
            class="tab-button"
            :class="{ 'active': currentFilters.tabType === 'pending'}"
            @click="switchTab('pending')"
        >
          <span class="tab-text">{{ $t('waiting_for_approval') }}</span>
        </button>

      </div>

      <div>
        <div class="actions-container">

          <ActionDropdown
              :placeholder="this.$t('promotion_codes')"
              :actions="promotionActions"
              :onAction="handlePromotionAction"
          />
<!--
          <button
              class="create-product-btn"
              @click="$router.push('/marketplace/create')"
          >
            {{$t('create_content')}}
          </button>
          -->
        </div>

      </div>
    </div>

    <div class="marketplace-main-content">
      <MarketplaceSidebarFilters
          v-if="shouldShowSidebar"
          :current-tab="getCurrentTab()"
          @filters-changed="handleSidebarFiltersChanged"
      />
      <div class="marketplace-content" :class="{ 'full-width': currentFilters.tabType === 'pending' || currentFilters.tabType === 'sales' }">
        <div v-if="currentFilters.tabType === 'pending'" class="pending-approval-section">
          <PendingApprovalTable
              :loading="isLoading"
              :items="tableData"
              @refresh-data="handlePage(currentPage)"
          />
          <Pagination
              v-if="tableData.total > 0"
              :current-page="currentPage"
              :total-items="tableData.total"
              :per-page="20"
              :on-page-change="handlePage"
          />
        </div>

        <MarketplaceComments
            v-else-if="currentFilters.tabType === 'comments'"
            :comments-data="commentsData"
            :analytics="commentsAnalytics"
            :is-loading="isLoading"
            :current-page="currentPage"
            @page-change="handlePage"
            @delete-comment="handleDeleteComment"
        />

        <div v-else-if="currentFilters.tabType === 'sales'" class="sales-section">
          <LoadingOverlay v-if="isLoading" />

          <template v-else>
            <MySalesTable
                v-if="tableData && tableData.length > 0"
                :items="tableData"
                :loading="false"
            />

            <div v-else class="no-data-container">
              <h3 class="no-data-found">{{ $t('no_sales_data') || 'Satış Verisi Bulunamadı' }}</h3>
            </div>

            <Pagination
                v-if="tableData && tableData.length > 0 && paginationData?.total > 20"
                :current-page="currentPage"
                :total-items="paginationData?.total || 0"
                :per-page="20"
                :on-page-change="handlePage"
            />
          </template>
        </div>
        <div v-else-if="currentFilters.tabType === 'purchased'" class="purchased-section" :key="'purchased-' + componentKey">
          <LoadingOverlay v-if="isLoading" />

          <template v-else>
            <div class="products-grid" v-if="tableData?.length > 0">
              <MarketplaceProductCard
                  v-for="product in tableData"
                  :key="'product-' + (product.uuid || product.id)"
                  :product="product"
                  @payment-success="onProductPaymentSuccess"
              />
            </div>

            <div v-else class="no-data-container">
              <span class="no-data-found">{{ $t('no_purchased_products') }}</span>
            </div>

            <div class="pagination-center" v-if="tableData?.length > 0">
              <Pagination
                  :current-page="currentPage"
                  :total-items="paginationData?.total || 0"
                  :per-page="20"
                  :on-page-change="handlePageDebounced"
              />
            </div>
          </template>
        </div>

        <div class="products-section" v-else>
          <div class="products-grid" v-if="!isLoading && tableData?.length > 0">
            <MarketplaceProductCard
                v-for="product in tableData"
                :key="`${product.uuid}-${product.isMyProduct}`"
                :product="product"
                @payment-success="onProductPaymentSuccess"
            />
          </div>

          <MarketplaceWelcomeBanner
              v-if="!isLoading && (!tableData || tableData.length === 0) && !hasActiveFilters"
              @share-course="shareCourse"
              @learn-more="learnMoreAboutMarketplace"
          />
          <MarketplaceEmptyBanner
              v-else-if="!isLoading && (!tableData || tableData.length === 0) && hasActiveFilters"
              :search-query="currentFilters.searchText || ''"
              :has-filters="hasActiveFilters"
              :popular-categories="popularCategories"
              @clear-filters="clearAllFilters"
              @browse-all="browseAllCourses"
              @category-selected="selectCategory"
          />

          <div class="pagination-div">

            <Pagination
                v-if="paginationData && paginationData.total > 0"
                :current-page="currentPage"
                :total-items="paginationData.total"
                :per-page="20"
                :on-page-change="handlePage"
            />

          </div>
        </div>
      </div>

      <LoadingOverlay :isLoading="isLoading" />
    </div>

    <PromotionCodeModal
        :isVisible="showPromotionModal"
        :editItem="selectedPromotionItem"
        @close="closePromotionModal"
        @success="handlePromotionSuccess"
    />

    <PromotionSuccessModal
        :isVisible="showSuccessModal"
        :promotionCode="createdPromotionCode"
        @close="closeSuccessModal"
        @view-codes="viewPromotionCodes"
    />
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import MarketplaceProductCard from "@/components/MarketplaceProductCard.vue";
import MarketplaceEmptyBanner from "@/components/MarketplaceEmptyBanner.vue";
import MarketplaceWelcomeBanner from "@/components/MarketplaceWelcomeBanner.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import Pagination from "@/components/Pagination.vue";
import marketplaceService from "@/service/marketplaceService";
import marketplaceCategoriesService from "@/service/marketplaceCategoriesService";
import marketplaceCommentService from "@/service/marketplaceCommentService";
import {handleErrorResponse} from "@/utils/utils";
import clickOutside from "@/directives/clickOutside";
import MarketplaceSidebarFilters from '@/components/MarketplaceSidebarFilters.vue';
import MarketplaceComments from '@/components/MarketplaceComments.vue';
import PendingApprovalTable from '@/components/tables/PendingApprovalTable.vue';
import MySalesTable from '@/components/tables/Marketplace/MySalesTable.vue';
import ActionDropdown from '@/components/ActionDropdown.vue';
import PromotionCodeModal from '@/components/modals/PromotionCodeModal.vue';
import PromotionSuccessModal from '@/components/modals/PromotionSuccessModal.vue';
import { Vue3Lottie } from 'vue3-lottie';
import confettiData from '@/assets/animations/confetti.json';

export default {
  name: 'Marketplace',
  directives: {
    clickOutside
  },
  components: {
    Layout,
    MarketplaceProductCard,
    MarketplaceEmptyBanner,
    MarketplaceWelcomeBanner,
    LoadingOverlay,
    Pagination,
    MarketplaceSidebarFilters,
    MarketplaceComments,
    PendingApprovalTable,
    MySalesTable,
    ActionDropdown,
    PromotionCodeModal,
    PromotionSuccessModal,
    Vue3Lottie
  },
  data() {
    return {
      isLoading: false,
      tableData: [],
      currentPage: 1,
      currentFilters: { onlyCompanies: 1 },
      popularCategories: [],
      allCategories: [],
      currentCategoryName: '',
      paginationData: null,
      commentsData: null,
      commentsAnalytics: null,
      promotionActions: [
        {
          name: this.$t('create_promo_code'),
          action: 'create'
        },
        {
          name: this.$t('my_promo_codes'),
          action: 'my-codes'
        }
      ],
      showPromotionModal: false,
      selectedPromotionItem: null,
      showSuccessModal: false,
      createdPromotionCode: '',
      showConfetti: false,
      confettiAnimation: confettiData,
      loadingTimeout: null
    }
  },
  watch: {
    'currentFilters.marketplaceCategoryId': {
      handler: async function(newCategoryId) {
        if (newCategoryId) {
          const localCategory = this.allCategories.find(cat => cat.uuid === newCategoryId);
          if (localCategory?.title) {
            this.currentCategoryName = localCategory.title;
          } else {
            this.currentCategoryName = await this.fetchCategoryName(newCategoryId);
          }
        } else {
          this.currentCategoryName = '';
        }
      },
      immediate: true
    }
  },
  async created() {
    await this.loadPopularCategories();
    this.syncFiltersFromRoute();
    this.handlePage(1);
  },
  beforeUnmount() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  },
  computed: {
    hasActiveFilters() {
      const filtersWithoutTab = { ...this.currentFilters };
      delete filtersWithoutTab.onlyCompanies;
      delete filtersWithoutTab.tabType;
      return Object.keys(filtersWithoutTab).length > 0;
    },
    shouldShowSidebar() {
      return this.currentFilters.tabType !== 'sales' && this.currentFilters.tabType !== 'pending';
    }
  },
  methods: {
    syncFiltersFromRoute() {
      const routeQuery = this.$route.query;
      const filters = { onlyCompanies: this.currentFilters.onlyCompanies || 1 };

      if (routeQuery.searchText) filters.searchText = routeQuery.searchText;
      if (routeQuery.marketplaceCategoryId) filters.marketplaceCategoryId = routeQuery.marketplaceCategoryId;
      if (routeQuery.moduleId) filters.moduleId = routeQuery.moduleId;
      if (routeQuery.currency) filters.currency = routeQuery.currency;
      if (routeQuery.minPrice) filters.minPrice = routeQuery.minPrice;
      if (routeQuery.maxPrice) filters.maxPrice = routeQuery.maxPrice;
      if (routeQuery.startDate) filters.startDate = routeQuery.startDate;
      if (routeQuery.endDate) filters.endDate = routeQuery.endDate;
      if (routeQuery.orderByPrice) filters.orderByPrice = routeQuery.orderByPrice;
      if (routeQuery.activeOnly === 'true') filters.activeOnly = 1;

      this.currentFilters = filters;
    },

    forceStopLoading() {
      this.isLoading = false;
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
        this.loadingTimeout = null;
      }
    },

    handlePage(page) {
      if (this.isLoading) return;

      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }

      this.isLoading = true;
      this.currentPage = page;

      this.loadingTimeout = setTimeout(() => {
        this.forceStopLoading();
      }, 10000);

      const finishLoading = () => {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
        this.isLoading = false;
      };

      if (this.currentFilters.tabType === 'comments') {
        this.handleCommentsTab(page, finishLoading);
      } else if (this.currentFilters.tabType === 'pending') {
        this.handlePendingTab(page, finishLoading);
      } else if (this.currentFilters.tabType === 'sales') {
        this.handleSalesTab(page, finishLoading);
      } else if (this.currentFilters.tabType === 'purchased') {
        this.handlePurchasedTab(page, finishLoading);
      } else if (this.currentFilters.onlyCompanies === 1) {
        this.handleMyProductsTab(page, finishLoading);
      } else {
        this.handleStoreTab(page, finishLoading);
      }
    },

    handleCommentsTab(page, finishLoading) {
      const commentParams = {
        page: page,
        per_page: 20,
        ...this.getFilterParamsWithoutOnlyCompanies()
      };
      marketplaceCommentService.getAllComments(commentParams)
          .then((res) => {
            this.commentsData = res.data?.data?.comments || [];
            this.commentsAnalytics = res.data?.data?.analytics || null;
          })
          .catch((error) => {
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(finishLoading);
    },

    handlePendingTab(page, finishLoading) {
      const pendingParams = { page, limit: 20 };
      marketplaceService.pendingProducts(pendingParams)
          .then((res) => {
            this.paginationData = res.data?.data || null;
            this.tableData = res.data?.data?.data || [];
          })
          .catch((error) => {
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(finishLoading);
    },

    handleSalesTab(page, finishLoading) {
      const salesParams = { page, limit: 20 };

      marketplaceService.mySales(salesParams)
          .then((res) => {

            this.paginationData = res.data?.data || null;
            const rawSalesData = res.data?.data?.data || [];

            if (!Array.isArray(rawSalesData)) {
              this.tableData = [];
              return;
            }

            if (rawSalesData.length === 0) {
              this.tableData = [];
              return;
            }

            this.tableData = rawSalesData.map((sale, index) => {

              if (!sale) {
                return null;
              }

              const processed = {
                uuid: sale.uuid || sale.id || `sale-${index}`,
                id: sale.id || sale.uuid,
                productUuid: sale.productUuid || sale.product?.uuid,
                product: {
                  images: sale.product?.images || [],
                  title: sale.product?.title || 'Untitled Product',
                  uuid: sale.product?.uuid || sale.productUuid
                },
                buyerCompany: {
                  logo: sale.buyerCompany?.logo || null,
                  name: sale.buyerCompany?.name || sale.invoiceInfo?.billing?.companyName || 'Unknown Buyer'
                },
                invoiceInfo: {
                  pricing: {
                    finalPrice: sale.invoiceInfo?.pricing?.finalPrice || sale.finalPrice || '0.00',
                    currency: sale.invoiceInfo?.pricing?.currency || sale.currency || 'TRY'
                  },
                  invoiceNumber: sale.invoiceInfo?.invoiceNumber || sale.invoiceNumber || 'N/A'
                },
                isCompleted: sale.isCompleted === true,
                createdAt: sale.createdAt || sale.created_at || new Date().toISOString()
              };

              return processed;
            }).filter(item => item !== null);

          })
          .catch((error) => {
            this.tableData = [];
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(() => {
            finishLoading();
          });
    },

    handlePurchasedTab(page, finishLoading) {
      const purchasedParams = { page, limit: 20 };

      marketplaceService.purchasedProducts(purchasedParams)
          .then((res) => {
            if (!res?.data) return;

            this.paginationData = res.data?.data || null;
            const rawData = res.data?.data?.data || [];

            this.tableData = rawData
                .filter(item => item?.product)
                .map(purchase => {
                  const productData = purchase.product;
                  return {
                    uuid: productData.uuid || purchase.uuid,
                    id: productData.id || purchase.id,
                    title: productData.title || 'Untitled',
                    description: productData.description || '',
                    price: productData.price || '0.00',
                    formattedPrice: productData.formattedPrice || `${productData.price} ${productData.currency}`,
                    currency: productData.currency || 'USD',
                    images: productData.images || [],
                    endDate: productData.endDate || null,
                    marketplaceModule: {
                      moduleName: productData.marketplaceModule?.moduleName || 'Unknown Module'
                    },
                    marketplaceCategory: {
                      title: productData.marketplaceCategory?.title || 'Uncategorized'
                    },
                    commentStats: {
                      averageRating: productData.commentStats?.averageRating || 0,
                      totalComments: productData.commentStats?.totalComments || 0
                    },
                    companyInfo: {
                      name: productData.companyInfo?.name || 'Unknown Company',
                      logo: productData.companyInfo?.logo || null
                    },
                    isMyProduct: false,
                    isPurchased: true,
                    isAccepted: true,
                    purchaseInfo: {
                      purchaseDate: purchase.formattedPurchaseDate || purchase.formattedCreatedAt,
                      purchaseStatus: purchase.purchaseStatus,
                      hasAccess: purchase.hasAccess,
                      invoiceNumber: purchase.invoiceNumber,
                      isFree: purchase.isFree,
                      finalPrice: purchase.formattedFinalPrice,
                      originalPrice: purchase.formattedOriginalPrice
                    }
                  };
                });
          })
          .catch((error) => {
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(finishLoading);
    },

    handleMyProductsTab(page, finishLoading) {
      const myProductParams = {
        page,
        limit: 20,
        ...this.currentFilters
      };
      delete myProductParams.onlyCompanies;
      delete myProductParams.tabType;

      marketplaceService.myProduct(myProductParams)
          .then((res) => {
            this.paginationData = res.data?.data || null;
            this.tableData = res.data?.data?.data || [];
          })
          .catch((error) => {
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(finishLoading);
    },

    handleStoreTab(page, finishLoading) {
      const storeParams = {
        page,
        limit: 20,
        ...this.currentFilters
      };
      delete storeParams.onlyCompanies;
      delete storeParams.tabType;

      marketplaceService.store(storeParams)
          .then((res) => {
            this.paginationData = res.data?.data || null;
            this.tableData = res.data?.data?.data || [];
          })
          .catch((error) => {
            handleErrorResponse(error, this.$snackbar);
          })
          .finally(finishLoading);
    },

    handleFiltersChanged(filters) {
      const mappedFilters = { ...filters };
      if (mappedFilters.search) {
        mappedFilters.searchText = mappedFilters.search;
        delete mappedFilters.search;
      }
      this.currentFilters = { ...this.currentFilters, ...mappedFilters };
      this.handlePage(1);
    },

    async loadPopularCategories() {
      try {
        const response = await marketplaceCategoriesService.getCategories({ page: 1, limit: 100 });
        this.allCategories = response.data?.data?.data || [];
        this.popularCategories = this.allCategories.slice(0, 5);
      } catch (error) {
        this.popularCategories = [];
        this.allCategories = [];
      }
    },

    clearAllFilters() {
      this.currentFilters = { onlyCompanies: 1 };
      this.handlePage(1);
    },

    browseAllCourses() {
      this.clearAllFilters();
    },

    selectCategory(category) {
      this.currentFilters = { ...this.currentFilters, marketplaceCategoryId: category.uuid };
      this.handlePage(1);
    },

    async fetchCategoryName(categoryId) {
      try {
        const response = await marketplaceCategoriesService.getCategoryById(categoryId);
        return response.data?.data?.title || this.$t('category_not_found');
      } catch (error) {
        return this.$t('category_not_found');
      }
    },

    switchTab(tabType) {
      this.forceStopLoading();

      const newFilters = {};
      if (tabType === 'comments') {
        newFilters.tabType = 'comments';
      } else if (tabType === 'pending') {
        newFilters.tabType = 'pending';
      } else if (tabType === 'sales') {
        newFilters.tabType = 'sales';
      } else if (tabType === 'purchased') {
        newFilters.tabType = 'purchased';
      } else if (tabType === 1) {
        newFilters.onlyCompanies = 1;
      } else {
        newFilters.onlyCompanies = 0;
      }

      this.currentFilters = newFilters;
      this.tableData = [];
      this.paginationData = null;

      setTimeout(() => {
        this.handlePage(1);
      }, 50);
    },

    getFilterParamsWithoutOnlyCompanies() {
      const params = { ...this.currentFilters };
      delete params.onlyCompanies;
      delete params.tabType;
      return params;
    },

    handleSidebarFiltersChanged(filters) {
      const currentTab = {
        onlyCompanies: this.currentFilters.onlyCompanies,
        tabType: this.currentFilters.tabType
      };
      this.currentFilters = { ...currentTab, ...filters };
      this.handlePage(1);
    },

    getCurrentTab() {
      if (this.currentFilters.onlyCompanies === 1 && !this.currentFilters.tabType) return 'myProducts';
      if (this.currentFilters.onlyCompanies === 0 && !this.currentFilters.tabType) return 'publicProducts';
      if (this.currentFilters.tabType === 'comments') return 'comments';
      if (this.currentFilters.tabType === 'purchased') return 'purchased';
      return 'publicProducts';
    },

    handleDeleteComment(comment) {
      if (confirm(this.$t('delete_comment_warning'))) {
        marketplaceCommentService.deleteComment(comment.id)
            .then(() => {
              this.$snackbar.add({
                type: 'success',
                text: this.$t('delete_comment_success')
              });
              this.handlePage(this.currentPage);
            })
            .catch((error) => {
              handleErrorResponse(error, this.$snackbar);
            });
      }
    },

    handlePromotionAction(actionType) {
      if (actionType === 'create') {
        this.selectedPromotionItem = null;
        this.showPromotionModal = true;
      } else if (actionType === 'my-codes') {
        this.$router.push('/marketplace/my-promotion-codes');
      }
    },

    closePromotionModal() {
      this.showPromotionModal = false;
      this.selectedPromotionItem = null;
    },

    handlePromotionSuccess(promotionData) {
      this.closePromotionModal();
      this.showConfetti = true;
      this.createdPromotionCode = promotionData.code || promotionData.promotionCode || this.$t('new_code');
      this.showSuccessModal = true;
    },

    onConfettiComplete() {
      this.showConfetti = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.createdPromotionCode = '';
    },

    viewPromotionCodes() {
      this.$router.push('/marketplace/my-promotion-codes');
    },

    shareCourse() {},
    learnMoreAboutMarketplace() {},

    onProductPaymentSuccess() {
      this.forceStopLoading();
      this.handlePage(1);
    }
  }
}
</script>

<style scoped>
.actions-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.create-product-btn {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  background: #101828;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  color: white;
}

.create-icon {
  font-size: 14px;
  color: white !important;
}

.category-filter-wrapper {
  position: relative;
  display: inline-block;
}

.category-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  background: white;
  border: 1px solid #d1d7dc;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1c1d1f;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-toggle-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(86, 36, 208, 0.3);
}

.category-icon {
  font-size: 16px;
}

/* Category Sidebar */
.category-sidebar {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-height: 600px;
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  overflow: visible;
  background: white;
  border: 1px solid #e1e5e9;
}

/* Marketplace Tabs */
.marketplace-tabs {
  display: flex;
  align-items: center;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  justify-content: space-between;
}

/* Tab Content Headers */
.sales-header,
.purchased-header {
  margin-bottom: 24px;
  text-align: center;
}

.sales-header h3,
.purchased-header h3 {
  font-size: 24px;
  font-weight: 600;
  color: #1c1d1f;
  margin: 0 0 8px 0;
}

.sales-header p,
.purchased-header p {
  font-size: 16px;
  color: #6a6f73;
  margin: 0;
}

.tab-button {
  display: flex;
  align-items: center;
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  white-space: nowrap;
}

.tab-button:hover:not(.disabled):not(.active) {
  background: rgba(108, 117, 125, 0.1);
}

.tab-button.active {
  background: #F2F4F7;
  color: #495057;
}

.tab-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  color: #adb5bd;
}

.tab-button.disabled:hover {
  background: transparent;
  color: #adb5bd;
}

.tab-text {
  font-size: 12px;
  font-family: 'euclid_medium', 'sans-serif';
  color: #333f54;
}

.tab-count {
  background: #e9ecef;
  color: #6c757d;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.tab-button.active .tab-count {
  background: #dee2e6;
  color: #495057;
}

.tab-button.disabled .tab-count {
  background: rgba(173, 181, 189, 0.2);
  color: #adb5bd;
}

.filter-chip-boolean .chip-label {
  color: rgba(255, 255, 255, 0.9);
}


.filter-chip-boolean .chip-value {
  color: white;
}

.filter-chip-boolean .chip-remove {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.filter-chip-boolean .chip-remove:hover {
  background: rgba(255, 255, 255, 0.3);
}

.marketplace-main-content {
  display: flex;
  gap: 32px;
  margin-top: 24px;
  position: relative;
}
.marketplace-content {
  flex: 1;
  min-width: 0;
  text-align: center;
  justify-content: center;
  align-items: center;
}

.marketplace-content.full-width {
  width: 100%;
  max-width: none;
}

.pending-approval-section {
  width: 100%;
}

.products-section {
  flex: 1;
  min-width: 0;
}

.pagination-center{
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.products-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e1e5e9;
}

.results-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.results-count {
  font-size: 14px;
  color: #747d8c;
  font-weight: 500;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

@media (max-width: 1200px) {

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 900px) {}

@media (max-width: 768px) {
  .actions-container {
    flex-wrap: wrap;
    width: 100%;
  }

  .category-sidebar > * {
    background: white;
    border-radius: 8px;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }

  @media (max-width: 768px) {

  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }


  .tab-button {
    padding: 8px 6px;
    font-size: 12px;
    gap: 4px;
    flex-direction: column;
  }

  .tab-text {
    font-size: 10px;
    text-align: center;
    line-height: 1.2;
  }
}

.page-title-h2{
  font-size: 20px;
  line-height: 28px;
  color: #040C2D;
  font-family: 'euclid_medium', sans-serif;

}
.page-title-desc{
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  font-family: 'euclid-regular', sans-serif;
}
.flex-row-div{
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap:10px
}

/* Marketplace Tabs */
/* Full Screen Confetti */
.confetti-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  background: rgba(0, 0, 0, 0.1) !important;
  pointer-events: none !important;
}

.confetti-overlay .lottie-container,
.confetti-overlay > div {
  max-width: 100vw;
  max-height: 100vh;
  width: 100%;
  height: 100%;
}

.pagination-div{
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.sales-section{
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.purchased-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
}

.purchased-section .products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  width: 100%;
  margin-bottom: 0; /* Pagination ile araya boşluk css'ten gelecek */
}

.purchased-section .pagination-center {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 32px; /* Üstten boşluk */
}

.purchased-section .no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  width: 100%;
}

@media (max-width: 1200px) {
  .purchased-section .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 900px) {
  .purchased-section .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .purchased-section .products-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .purchased-section .pagination-center {
    padding-top: 24px;
  }
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.no-data-found {
  font-size: 16px;
  color: #6a6f73;
}
</style>
